import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiDollarSign, FiClock, FiAlertCircle, FiCreditCard } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import useReportData from "../../useReportData";

const VendorPaymentAgingLedgerReport = () => {
  const gridRef = useRef(null);
  const [data] = useReportData("VendorAging");
  const [searchTerm, setSearchTerm] = useState("");
  const [creditTermsFilter, setCreditTermsFilter] = useState("all");
  const [agingFilter, setAgingFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalOutstanding = data.reduce((s, r) => s + (r.balanceOutstanding || 0), 0);
    const totalInvoiced = data.reduce((s, r) => s + (r.totalInvoiced || 0), 0);
    const totalNetPaid = data.reduce((s, r) => s + (r.netPaid || 0), 0);
    const criticalOverdue = data.reduce((s, r) => s + (r.bucketOver45 || 0), 0);

    return {
      totalOutstanding,
      totalInvoiced,
      totalNetPaid,
      criticalOverdue,
    };
  }, [data]);

  const uniqueTerms = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.creditTerms).filter(Boolean)));
  }, [data]);

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Vendor Name & NTN",
        field: "vendorName",
        colId: "vendorName",
        flex: 1.8,
        minWidth: 200,
        cellRenderer: (p) => (
          <div>
            <span style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</span>
            <div style={{ color: "#64748b", fontSize: "11px" }}>NTN: {p.data?.ntn}</div>
          </div>
        ),
      },
      {
        headerName: "Credit Terms",
        field: "creditTerms",
        colId: "creditTerms",
        flex: 1.2,
        minWidth: 130,
        cellRenderer: (p) => (
          <span
            style={{
              background: "#f1f5f9",
              color: "#334155",
              padding: "2px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            {p.value}
          </span>
        ),
      },
      {
        headerName: "Total Invoiced (PKR)",
        field: "totalInvoiced",
        colId: "totalInvoiced",
        flex: 1.4,
        minWidth: 150,
        valueFormatter: (p) => (p.value ? `PKR ${Number(p.value).toLocaleString()}` : "PKR 0"),
      },
      {
        headerName: "Advance Paid / Adj (PKR)",
        field: "advancePaid",
        colId: "advancePaid",
        flex: 1.4,
        minWidth: 150,
        cellRenderer: (p) => (
          <span style={{ color: "#0284c7", fontWeight: 600 }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Net Paid to Date (PKR)",
        field: "netPaid",
        colId: "netPaid",
        flex: 1.4,
        minWidth: 150,
        cellRenderer: (p) => (
          <span style={{ color: "#16a34a", fontWeight: 600 }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Balance Outstanding (PKR)",
        field: "balanceOutstanding",
        colId: "balanceOutstanding",
        flex: 1.6,
        minWidth: 170,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0f172a" }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "0 – 15 Days",
        field: "bucket0to15",
        colId: "bucket0to15",
        flex: 1.2,
        minWidth: 130,
        cellRenderer: (p) => (
          <span style={{ color: "#334155" }}>
            {p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"}
          </span>
        ),
      },
      {
        headerName: "16 – 30 Days",
        field: "bucket16to30",
        colId: "bucket16to30",
        flex: 1.2,
        minWidth: 130,
        cellRenderer: (p) => (
          <span style={{ color: "#d97706", fontWeight: p.value ? 600 : 400 }}>
            {p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"}
          </span>
        ),
      },
      {
        headerName: "31 – 45 Days",
        field: "bucket31to45",
        colId: "bucket31to45",
        flex: 1.2,
        minWidth: 130,
        cellRenderer: (p) => (
          <span style={{ color: "#ea580c", fontWeight: p.value ? 600 : 400 }}>
            {p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"}
          </span>
        ),
      },
      {
        headerName: "Over 45 Days",
        field: "bucketOver45",
        colId: "bucketOver45",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => {
          if (!p.value) {
            return <span style={{ color: "#94a3b8" }}>—</span>;
          }
          return (
            <span
              style={{
                color: "#dc2626",
                fontWeight: 700,
                background: "#fef2f2",
                padding: "2px 6px",
                borderRadius: "4px",
              }}
            >
              PKR {Number(p.value).toLocaleString()}
            </span>
          );
        },
      },
    ];
  }, []);

  // Filter logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matches =
          (row.vendorName && row.vendorName.toLowerCase().includes(q)) ||
          (row.ntn && row.ntn.toLowerCase().includes(q)) ||
          (row.creditTerms && row.creditTerms.toLowerCase().includes(q));
        if (!matches) {
          return false;
        }
      }

      if (creditTermsFilter !== "all" && row.creditTerms !== creditTermsFilter) {
        return false;
      }

      if (agingFilter === "over45" && (!row.bucketOver45 || row.bucketOver45 <= 0)) {
        return false;
      }
      if (agingFilter === "31to45" && (!row.bucket31to45 || row.bucket31to45 <= 0)) {
        return false;
      }
      if (agingFilter === "16to30" && (!row.bucket16to30 || row.bucket16to30 <= 0)) {
        return false;
      }
      if (agingFilter === "0to15" && (!row.bucket0to15 || row.bucket0to15 <= 0)) {
        return false;
      }

      return true;
    });
  }, [data, searchTerm, creditTermsFilter, agingFilter]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
    }),
    [],
  );

  const toggleColumn = useCallback((colId) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      if (gridRef.current?.api) {
        gridRef.current.api.setColumnsVisible([colId], next[colId]);
      }
      return next;
    });
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Vendor_Payment_Aging_Ledger_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("Vendor payment aging ledger exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL OUTSTANDING"
            value={`PKR ${(kpis.totalOutstanding / 1000000).toFixed(2)}M`}
            subtitle={`PKR ${kpis.totalOutstanding.toLocaleString()} payable`}
            icon={FiDollarSign}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL INVOICED VALUE"
            value={`PKR ${(kpis.totalInvoiced / 1000000).toFixed(2)}M`}
            subtitle="Cumulative supplier billings"
            icon={FiCreditCard}
            color="#0284c7"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="NET PAID TO DATE"
            value={`PKR ${(kpis.totalNetPaid / 1000000).toFixed(2)}M`}
            subtitle="Disbursed vendor payments"
            icon={FiClock}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="CRITICAL OVERDUE (>45 DAYS)"
            value={`PKR ${(kpis.criticalOverdue / 1000000).toFixed(2)}M`}
            subtitle="High risk supplier hold alerts"
            icon={FiAlertCircle}
            color="#dc2626"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (Search First, then Date, then other filters) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Vendor Payment Aging"
            >
              Vendor Payment Aging
            </div>
            {/* Search Filter FIRST */}
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search vendor, NTN..."
            />

            {/* Date Filter SECOND */}
            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            {/* Aging Bucket Filter THIRD */}
            <div className="d-flex align-items-center" style={{ maxWidth: "180px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={agingFilter}
                  onChange={(e) => setAgingFilter(e.target.value)}
                >
                  <option value="all">All Aging Buckets</option>
                  <option value="0to15">0 – 15 Days</option>
                  <option value="16to30">16 – 30 Days</option>
                  <option value="31to45">31 – 45 Days</option>
                  <option value="over45">Over 45 Days</option>
                </select>
                <label>Aging Bucket</label>
              </div>
            </div>

            {/* Credit Terms Filter */}
            <div className="d-flex align-items-center" style={{ maxWidth: "180px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={creditTermsFilter}
                  onChange={(e) => setCreditTermsFilter(e.target.value)}
                >
                  <option value="all">All Credit Terms</option>
                  {uniqueTerms.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <label>Credit Terms</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              animateRows={true}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorPaymentAgingLedgerReport;
